// общие расчёты таблиц приложения 21 (вкладки «Расходы» и «Показатели»)
import { App21View, IApp21ExpenseRow, IApp21KrRow, IApp21PprIndicatorRow, IApp21PprItem } from './app21InputForm';

// элемент списка причин для multiselect
export interface IReasonOption {
    value: string;
    key: number;
}

// ячейка строки таблицы (графы 1-6)
export interface IRowValue {
    value: string | number;
    key: string;
}

// точность сравнения отклонения с нулём — как на экране
export const MONEY_PRECISION = 1;     // суммы, тыс. теңге (formatNumber — toFixed(1))
export const INDICATOR_PRECISION = 6; // КР и ППР (formatPlain — округление до 1e-6)

// отклонение ≠ 0 с учётом отображаемой точности; без плана или факта отклонения нет
export const hasDeviation = (plan: number | null, fact: number | null, precision: number): boolean => {
    if (plan === null || fact === null) return false;
    return Math.round((fact - plan) * 10 ** precision) !== 0;
};

// причина обязательна: отклонение ≠ 0 или факт не введён при ненулевом плане;
// план 0 или не задан при пустом факте — не обязательна
export const isReasonRequired = (plan: number | null, fact: number | null, precision: number): boolean => {
    if (fact === null) {
        return plan !== null && Math.round(plan * 10 ** precision) !== 0;
    }
    return hasDeviation(plan, fact, precision);
};

// причина обязательна, а не выбрана
export const isReasonMissing = (plan: number | null, fact: number | null, reasonId: number | null, precision: number): boolean =>
    reasonId === null && isReasonRequired(plan, fact, precision);

// таблица строки: budget_execution_application21 / _kr / _ppr
export type App21Table = 'expenses' | 'kr' | 'ppr';

// ключ строки в наборе подсвеченных полей причины
export const reasonKey = (table: App21Table, id: number): string => `${table}:${id}`;

// строка без обязательной причины и вкладка, на которой она показана;
// ppr — подпрограмма строки на «Показателях» (для перехода к ней)
export interface IMissingReason {
    view: App21View;
    table: App21Table;
    id: number;
    ppr?: number | null;
}

// bip_code без пробелов; пустой — null (как _normalize_bip_code на бэке)
export const normalizeBipCode = (bip: string | null | undefined): string | null => (bip ?? '').trim() || null;

/**
 * Проверка причин перед сохранением (NTF.eAk.MONF01.02).
 * «Расходы»: программа в целом и все КР; «Показатели»: выбранная подпрограмма и её ППР.
 * Порядок результата — сначала «Расходы»: по первому элементу выбирается вкладка для перехода.
 */
export const collectMissingReasons = (
    expenses: IApp21ExpenseRow[],
    kr: IApp21KrRow[],
    indicators: IApp21PprIndicatorRow[],
    selectedPpr: IApp21PprItem | null,
): IMissingReason[] => {
    const missing: IMissingReason[] = [];

    const total = expenses.find(r => r.ppr === null) ?? null;
    if (total && isReasonMissing(total.plan, total.fact, total.reason_id, MONEY_PRECISION)) {
        missing.push({ view: 'expenses', table: 'expenses', id: total.id });
    }
    for (const row of kr) {
        if (isReasonMissing(row.plan, row.fact, row.reason_id, INDICATOR_PRECISION)) {
            missing.push({ view: 'expenses', table: 'kr', id: row.id });
        }
    }

    // подпрограмм нет в списке — на «Показателях» проверять нечего
    if (!selectedPpr) return missing;
    const ppr = selectedPpr.ppr;

    // у БП без подпрограмм это та же строка ppr = null — уже учтена на «Расходах»
    const pprRow = expenses.find(r => r.ppr === ppr) ?? null;
    if (pprRow && pprRow !== total && isReasonMissing(pprRow.plan, pprRow.fact, pprRow.reason_id, MONEY_PRECISION)) {
        missing.push({ view: 'indicators', table: 'expenses', id: pprRow.id, ppr });
    }
    for (const row of indicators) {
        if (row.ppr === ppr && isReasonMissing(row.plan, row.fact, row.reason_id, INDICATOR_PRECISION)) {
            missing.push({ view: 'indicators', table: 'ppr', id: row.id, ppr });
        }
    }
    return missing;
};

/**
 * Проверка перед сохранением после «Обновить данные БП»: «Расходы» и выбранная подпрограмма,
 * как collectMissingReasons, плюс подпрограммы, в которых пользователь менял факт или причину (changedPprs).
 * Порядок: «Расходы», выбранная подпрограмма, остальные по порядку списка.
 */
export const collectMissingReasonsForPprs = (
    expenses: IApp21ExpenseRow[],
    kr: IApp21KrRow[],
    indicators: IApp21PprIndicatorRow[],
    pprList: IApp21PprItem[],
    selectedPpr: IApp21PprItem | null,
    changedPprs: Set<number | null>,
): IMissingReason[] => {
    const missing = collectMissingReasons(expenses, kr, indicators, selectedPpr);
    const seen = new Set(missing.map(m => reasonKey(m.table, m.id)));
    for (const item of pprList) {
        if (item === selectedPpr || !changedPprs.has(item.ppr)) continue;
        for (const m of collectMissingReasons(expenses, [], indicators, item)) {
            const key = reasonKey(m.table, m.id);
            if (!seen.has(key)) {
                seen.add(key);
                missing.push(m);
            }
        }
    }
    return missing;
};


/**
 * ФЛК формы для согласования: есть ли отклонение без причины в любой строке формы —
 * по программе, всем подпрограммам, КР и ППР (в отличие от проверки при сохранении, без учёта выбранной ППР)
 */
export const hasAnyMissingReason = (
    expenses: IApp21ExpenseRow[],
    kr: IApp21KrRow[],
    indicators: IApp21PprIndicatorRow[],
): boolean =>
    expenses.some(r => isReasonMissing(r.plan, r.fact, r.reason_id, MONEY_PRECISION))
    || kr.some(r => isReasonMissing(r.plan, r.fact, r.reason_id, INDICATOR_PRECISION))
    || indicators.some(r => isReasonMissing(r.plan, r.fact, r.reason_id, INDICATOR_PRECISION));

// значение поля факта: пустое, нечисловое или отрицательное — факт не введён (null)
export const parseFact = (value: string): number | null => {
    const num = Number(value);
    return value === '' || !Number.isFinite(num) || num < 0 ? null : num;
};

// отрицательный факт недопустим
const BLOCKED_FACT_KEYS = ['-', '+', 'e', 'E'];
export const blockNegativeFactKey = (event: KeyboardEvent): void => {
    if (BLOCKED_FACT_KEYS.includes(event.key)) {
        event.preventDefault();
    }
};

// вставка из буфера: текст с минусом не вставляется
export const blockNegativeFactPaste = (event: ClipboardEvent): void => {
    if ((event.clipboardData?.getData('text') ?? '').includes('-')) {
        event.preventDefault();
    }
};

// графы 1-6: отклонение и % считаются только при наличии и плана, и факта
export const rowValues = (name: string, unit: string, plan: number | null, fact: number | null): IRowValue[] => {
    let dev: number | string = '-';
    let percent: number | string = '-';
    if (plan !== null && fact !== null) {
        dev = fact - plan;
        // при нулевом плане процент не считаем
        percent = plan ? (fact / plan) * 100 : '-';
    }

    return [
        { value: name, key: 'name' },
        { value: unit, key: 'unit' },
        { value: plan ?? '-', key: 'plan' },
        { value: fact ?? '-', key: 'fact' },
        { value: dev, key: 'deviation' },
        { value: percent, key: 'percent' },
    ];
};
